<template>
  <div class="relative">
    <!-- Date Input Display -->
    <div
      class="input-field cursor-pointer flex items-center justify-between"
      @click="toggleCalendar"
    >
      <span :class="{ 'text-gray-400': !modelValue }">
        {{ displayValue || placeholder }}
      </span>
      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
      </svg>
    </div>

    <!-- Calendar Popup -->
    <div
      v-if="isOpen"
      class="absolute top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
      :class="{ 'left-0': !isRTL, 'right-0': isRTL }"
      style="width: 640px;"
    >
      <!-- Top Controls -->
      <div class="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div class="flex items-center justify-between mb-2">
          <!-- Calendar Type Toggle -->
          <div class="flex bg-gray-200 rounded-lg p-1">
            <button
              type="button"
              class="px-3 py-1 text-xs font-medium rounded-md transition-colors"
              :class="{
                'bg-white text-gray-900 shadow-sm': calendarType === 'jalali',
                'text-gray-600 hover:text-gray-900': calendarType !== 'jalali'
              }"
              @click="setCalendarType('jalali')"
            >
              {{ t('datePicker.jalali') }}
            </button>
            <button
              type="button"
              class="px-3 py-1 text-xs font-medium rounded-md transition-colors"
              :class="{
                'bg-white text-gray-900 shadow-sm': calendarType === 'gregorian',
                'text-gray-600 hover:text-gray-900': calendarType !== 'gregorian'
              }"
              @click="setCalendarType('gregorian')"
            >
              {{ t('datePicker.gregorian') }}
            </button>
          </div>

          <!-- Go to Today Button -->
          <button
            type="button"
            class="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
            @click="goToToday"
          >
            {{ t('datePicker.goToToday') }}
          </button>
        </div>
      </div>

      <!-- Two Month Headers -->
      <div class="bg-gradient-to-r from-blue-500 to-indigo-600 px-1 py-1">
        <div class="flex items-center justify-between text-white">
          <!-- Previous Month Button -->
          <button
            type="button"
            class="p-1 hover:bg-white/20 rounded-full transition-colors"
            @click="previousMonth"
          >
            <svg class="w-4 h-4" :class="{ 'rotate-180': isRTL }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
          </button>

          <!-- First Month -->
          <div class="text-center flex-1">
            <div class="text-lg font-bold">
              {{ firstMonthName }}
            </div>
            <div class="text-sm opacity-90">
              {{ firstMonthYear }}
            </div>
          </div>

          <!-- Separator -->
          <div class="mx-4 w-px h-8 bg-white/30"/>

          <!-- Second Month -->
          <div class="text-center flex-1">
            <div class="text-lg font-bold">
              {{ secondMonthName }}
            </div>
            <div class="text-sm opacity-90">
              {{ secondMonthYear }}
            </div>
          </div>

          <!-- Next Month Button -->
          <button
            type="button"
            class="p-1 hover:bg-white/20 rounded-full transition-colors"
            @click="nextMonth"
          >
            <svg class="w-4 h-4" :class="{ 'rotate-180': isRTL }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>

      <div class="p-3">
        <!-- Two Month Grid -->
        <div class="grid grid-cols-2 gap-4">
          <!-- First Month -->
          <div>
            <!-- Weekday Headers -->
            <div class="grid grid-cols-7 gap-1 mb-1">
              <div
                v-for="day in weekDayHeaders"
                :key="`first-${day}`"
                class="text-center text-xs font-semibold text-gray-600 py-1"
              >
                {{ day }}
              </div>
            </div>

            <!-- First Month Days -->
            <div class="grid grid-cols-7 gap-1">
              <button
                v-for="day in firstMonthDays"
                :key="`first-month-${day.date}`"
                type="button"
                class="day-cell"
                :class="{
                  'bg-orange-400 text-white font-bold shadow-lg': day.isToday,
                  'bg-blue-100 text-blue-800 ring-2 ring-blue-300': day.isSelected && !day.isToday,
                  'bg-gray-100 text-gray-900': tempSelectedDate === day.date && !day.isSelected && !day.isToday,
                  'hover:bg-gray-100': day.isCurrentMonth && !day.isSelected && !day.isToday && tempSelectedDate !== day.date,
                  'text-red-500': day.isHoliday && !day.isSelected && !day.isToday,
                  'text-gray-300': !day.isCurrentMonth
                }"
                :data-test="day.date"
                @click="selectTempDate(day.date)"
              >
                <span class="text-xs font-medium">{{ day.display }}</span>
              </button>
            </div>
          </div>

          <!-- Second Month -->
          <div>
            <!-- Weekday Headers -->
            <div class="grid grid-cols-7 gap-1 mb-1">
              <div
                v-for="day in weekDayHeaders"
                :key="`second-${day}`"
                class="text-center text-xs font-semibold text-gray-600 py-1"
              >
                {{ day }}
              </div>
            </div>

            <!-- Second Month Days -->
            <div class="grid grid-cols-7 gap-1">
              <button
                v-for="day in secondMonthDays"
                :key="`second-month-${day.date}`"
                type="button"
                class="day-cell"
                :class="{
                  'bg-orange-400 text-white font-bold shadow-lg': day.isToday,
                  'bg-blue-100 text-blue-800 ring-2 ring-blue-300': day.isSelected && !day.isToday,
                  'bg-gray-100 text-gray-900': tempSelectedDate === day.date && !day.isSelected && !day.isToday,
                  'hover:bg-gray-100': day.isCurrentMonth && !day.isSelected && !day.isToday && tempSelectedDate !== day.date,
                  'text-red-500': day.isHoliday && !day.isSelected && !day.isToday,
                  'text-gray-300': !day.isCurrentMonth
                }"
                :data-test="day.date"
                @click="selectTempDate(day.date)"
              >
                <span class="text-xs font-medium">{{ day.display }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer with confirmation -->
      <div v-if="!props.closeOnSelect" class="bg-gray-50 px-4 py-3 border-t border-gray-200">
        <!-- Selected Date Display -->
        <div v-if="tempSelectedDate" class="text-center text-xs text-gray-600 mb-2">
          {{ t('datePicker.selected') }}{{ t(':') }} {{ formatSelectedDate(tempSelectedDate) }}
        </div>

        <!-- Confirm Button -->
        <button
          type="button"
          class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
          :disabled="!tempSelectedDate"
          @click="confirmSelection"
        >
          {{ t('datePicker.confirm') }}
        </button>
      </div>
    </div>

    <!-- Overlay to close calendar -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-40"
      @click="closeCalendar"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from '#i18n'
import moment from 'jalali-moment'

interface Props {
  modelValue: string
  placeholder?: string
  minDate?: string
  maxDate?: string
  closeOnSelect?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  minDate: '',
  maxDate: '',
  closeOnSelect: true
})

const emit = defineEmits<Emits>()
const { t, locale } = useI18n()

// Reactive state
const isOpen = ref(false)
const currentDate = ref(moment())
const selectedDate = ref<moment.Moment | null>(null)
const tempSelectedDate = ref<string | null>(null)
const calendarType = ref<'jalali' | 'gregorian'>('jalali') // Independent calendar type

// Initialize selected date from modelValue
onMounted(() => {
  if (props.modelValue) {
    selectedDate.value = moment(props.modelValue, 'YYYY-MM-DD')
    currentDate.value = selectedDate.value.clone()
    tempSelectedDate.value = props.modelValue
  }

  // Set default calendar type based on locale, but allow independent change
  calendarType.value = locale.value === 'fa' ? 'jalali' : 'gregorian'
})

// Computed properties
const isRTL = computed(() => locale.value === 'fa')
const isJalali = computed(() => calendarType.value === 'jalali')

const displayValue = computed(() => {
  if (!props.modelValue) return ''

  const date = moment(props.modelValue, 'YYYY-MM-DD')

  if (isJalali.value) {
    return convertToLocalNumbers(date.locale('fa').format('YYYY/MM/DD'))
  } else {
    return date.locale('en').format('YYYY/MM/DD')
  }
})

// Helper function to get month name
const getMonthName = (date: moment.Moment): string => {
  if (isJalali.value) {
    const monthKeys = [
      'farvardin', 'ordibehesht', 'khordad', 'tir', 'mordad', 'shahrivar',
      'mehr', 'aban', 'azar', 'dey', 'bahman', 'esfand'
    ]
    const monthIndex = date.locale('fa').month()
    return t(`dynamic.months.${monthKeys[monthIndex] || monthKeys[0]}`)
  } else {
    const gregorianMonthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ]
    const monthIndex = date.locale('en').month()
    return t(`dynamic.months.${gregorianMonthKeys[monthIndex]}`)
  }
}

// Helper function to get year
const getYear = (date: moment.Moment): string => {
  if (isJalali.value) {
    return convertToLocalNumbers(date.locale('fa').format('YYYY'))
  } else {
    return date.locale('en').format('YYYY')
  }
}

// First month (current)
const firstMonthName = computed(() => getMonthName(currentDate.value))
const firstMonthYear = computed(() => getYear(currentDate.value))

// Second month (next month)
const secondMonth = computed(() => {
  return currentDate.value.clone().add(1, 'month')
})

const secondMonthName = computed(() => getMonthName(secondMonth.value))
const secondMonthYear = computed(() => getYear(secondMonth.value))

const weekDayHeaders = computed(() => {
  if (isJalali.value) {
    return [
      t('datePicker.weekdays.short.saturday'),
      t('datePicker.weekdays.short.sunday'),
      t('datePicker.weekdays.short.monday'),
      t('datePicker.weekdays.short.tuesday'),
      t('datePicker.weekdays.short.wednesday'),
      t('datePicker.weekdays.short.thursday'),
      t('datePicker.weekdays.short.friday')
    ]
  } else {
    return [
      t('datePicker.weekdays.short.sunday'),
      t('datePicker.weekdays.short.monday'),
      t('datePicker.weekdays.short.tuesday'),
      t('datePicker.weekdays.short.wednesday'),
      t('datePicker.weekdays.short.thursday'),
      t('datePicker.weekdays.short.friday'),
      t('datePicker.weekdays.short.saturday')
    ]
  }
})

// Generate calendar days for a specific month
const generateMonthDays = (monthDate: moment.Moment): Array<any> => {
  const today = moment()
  const days = []

  // Set the locale for the month calculations
  const calendarLocale = isJalali.value ? 'fa' : 'en'
  const workingDate = monthDate.clone().locale(calendarLocale)
  const todayInLocale = today.clone().locale(calendarLocale)

  // Get the first and last day of the month
  const firstDayOfMonth = workingDate.clone().startOf('month')
  const lastDayOfMonth = workingDate.clone().endOf('month')

  // Calculate the start of the calendar grid
  const startOfCalendar = firstDayOfMonth.clone().startOf('week')
  const endOfCalendar = lastDayOfMonth.clone().endOf('week')

  // For Jalali calendar, adjust week start (Saturday = 0 in Jalali)
  if (isJalali.value) {
    // Adjust for Jalali week starting on Saturday
    const dayOfWeek = firstDayOfMonth.day()
    const adjustedStart = firstDayOfMonth.clone().subtract((dayOfWeek + 1) % 7, 'days')
    const adjustedEnd = lastDayOfMonth.clone().add(6 - (lastDayOfMonth.day() + 1) % 7, 'days')

    const current = adjustedStart.clone()
    while (current.isSameOrBefore(adjustedEnd)) {
      const isCurrentMonth = current.month() === workingDate.month()
      const isHoliday = isJalali.value && current.day() === 5 // Friday in Jalali

      days.push({
        date: current.clone().locale('en').format('YYYY-MM-DD'),
        display: convertToLocalNumbers(current.format('D')),
        isCurrentMonth,
        isToday: current.isSame(todayInLocale, 'day'),
        isSelected: selectedDate.value ? current.isSame(selectedDate.value.clone().locale(calendarLocale), 'day') : false,
        isHoliday
      })

      current.add(1, 'day')
    }
  } else {
    // Standard Gregorian calendar logic
    const current = startOfCalendar.clone()
    while (current.isSameOrBefore(endOfCalendar)) {
      const isCurrentMonth = current.month() === workingDate.month()

      days.push({
        date: current.format('YYYY-MM-DD'),
        display: current.format('D'),
        isCurrentMonth,
        isToday: current.isSame(todayInLocale, 'day'),
        isSelected: selectedDate.value ? current.isSame(selectedDate.value, 'day') : false,
        isHoliday: false
      })

      current.add(1, 'day')
    }
  }

  return days
}

// Calendar days for both months
const firstMonthDays = computed(() => generateMonthDays(currentDate.value))
const secondMonthDays = computed(() => generateMonthDays(secondMonth.value))

// Helper functions
const convertToLocalNumbers = (str: string): string => {
  if (!isJalali.value) return str

  const persianNumbers = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
  return str.replace(/\d/g, (digit) => persianNumbers[parseInt(digit)])
}

const formatSelectedDate = (dateString: string): string => {
  const date = moment(dateString, 'YYYY-MM-DD')

  if (isJalali.value) {
    const formatted = date.locale('fa').format('D MMMM YYYY')
    return convertToLocalNumbers(formatted)
  } else {
    return date.locale('en').format('MMMM D, YYYY')
  }
}

// Calendar methods
const toggleCalendar = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    tempSelectedDate.value = props.modelValue || null
  }
}

const closeCalendar = () => {
  isOpen.value = false
  tempSelectedDate.value = null
}

const setCalendarType = (type: 'jalali' | 'gregorian') => {
  calendarType.value = type
}

const previousMonth = () => {
  currentDate.value = currentDate.value.clone().subtract(1, 'month')
}

const nextMonth = () => {
  currentDate.value = currentDate.value.clone().add(1, 'month')
}

const selectTempDate = (dateString: string) => {
  tempSelectedDate.value = dateString

  if (props.closeOnSelect) {
    confirmSelection()
  }
}

const confirmSelection = () => {
  if (tempSelectedDate.value) {
    selectedDate.value = moment(tempSelectedDate.value, 'YYYY-MM-DD')
    emit('update:modelValue', tempSelectedDate.value)
    closeCalendar()
  }
}

const goToToday = () => {
  const today = moment()
  currentDate.value = today.clone()
  tempSelectedDate.value = today.format('YYYY-MM-DD')
}

// Handle escape key
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeCalendar()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<style scoped>
.day-cell {
  @apply relative h-8 w-full flex flex-col items-center justify-center rounded-md transition-all duration-200 text-center;
  min-height: 2rem;
}

.day-cell:hover {
  @apply transform scale-105;
}

/* Holiday styling for Persian calendar */
.day-cell.text-red-500 {
  @apply bg-red-50;
}

/* Today styling */
.day-cell.bg-orange-400 {
  @apply ring-2 ring-orange-300;
}

/* Disabled button styling */
.day-cell:disabled {
  @apply opacity-50 cursor-not-allowed;
}

button:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>
